import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PatreonNav from "../components/PatreonNav";
import PatreonFooter from "../components/PatreonFooter";

interface PricingTier {
  name: string;
  price: string;
  description: string;
  features: string[];
  popular?: boolean;
  cta: string;
  link: string;
}

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");

  const tiers: PricingTier[] = [
    {
      name: "Lite",
      price: billingCycle === "monthly" ? "$0" : "$0",
      description: "Perfect for getting started with your creative journey",
      features: [
        "5% platform fee + payment processing fees",
        "Unlimited posts",
        "Basic analytics",
        "Community features",
        "Mobile app access",
        "Email support"
      ],
      cta: "Get Started Free",
      link: "/register"
    },
    {
      name: "Pro",
      price: billingCycle === "monthly" ? "$8" : "$80",
      description: "For creators ready to grow their community",
      features: [
        "0% platform fee + payment processing fees",
        "Everything in Lite, plus:",
        "Advanced analytics & insights",
        "Merchandise integration",
        "Priority support",
        "Custom branding options",
        "Promo tools & discounts",
        "Unlimited digital downloads"
      ],
      popular: true,
      cta: "Start Pro Trial",
      link: "/register?plan=pro"
    },
    {
      name: "Premium",
      price: billingCycle === "monthly" ? "$25" : "$250",
      description: "For established creators scaling their business",
      features: [
        "0% platform fee + payment processing fees",
        "Everything in Pro, plus:",
        "Dedicated account manager",
        "Custom integrations",
        "API access",
        "Advanced team features",
        "White-label options",
        "Custom onboarding"
      ],
      cta: "Contact Sales",
      link: "/contact"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Pricing | Patreon</title>
        <meta name="description" content="Compare Patreon platform fees: Lite, Pro, and Premium plans." />
        <link rel="canonical" href={window.location.origin + "/pricing"} />
        <meta property="og:title" content="Pricing | Patreon" />
        <meta property="og:description" content="Plans that scale with your community" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pricing | Patreon" />
        <meta name="twitter:description" content="Plans that scale with your community" />
      </Helmet>

      <PatreonNav />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            className="text-6xl md:text-7xl font-bold mb-6"
            style={{ fontFamily: "var(--patreon-font-primary)" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Plans that scale with{" "}
            <span style={{ color: "var(--patreon-coral)" }}>your community</span>
          </motion.h1>
          
          <motion.p
            className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Choose the plan that fits your needs. Start for free and upgrade as you grow.
          </motion.p>

          {/* Billing Toggle */}
          <motion.div
            className="flex items-center justify-center gap-4 mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className={billingCycle === "monthly" ? "font-semibold" : "text-gray-500"}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === "monthly" ? "annual" : "monthly")}
              className="relative w-14 h-7 rounded-full transition-colors"
              style={{ backgroundColor: billingCycle === "annual" ? "var(--patreon-coral)" : "#e5e7eb" }}
            >
              <span
                className="absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform"
                style={{ transform: billingCycle === "annual" ? "translateX(28px)" : "translateX(0)" }}
              />
            </button>
            <span className={billingCycle === "annual" ? "font-semibold" : "text-gray-500"}>
              Annual <span className="text-green-600 text-sm">(Save 17%)</span>
            </span>
          </motion.div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="pb-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {tiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                className={`relative rounded-3xl p-8 ${
                  tier.popular
                    ? "bg-black text-white shadow-2xl scale-105"
                    : "bg-gray-50 border border-gray-200"
                }`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
              >
                {tier.popular && (
                  <div
                    className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-sm font-semibold text-white"
                    style={{ backgroundColor: "var(--patreon-coral)" }}
                  >
                    Most Popular
                  </div>
                )}

                <h3 className="text-3xl font-bold mb-2">{tier.name}</h3>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-5xl font-bold">{tier.price}</span>
                  {tier.price !== "$0" && (
                    <span className={tier.popular ? "text-gray-400" : "text-gray-500"}>
                      /{billingCycle === "monthly" ? "month" : "year"}
                    </span>
                  )}
                </div>
                <p className={`mb-8 ${tier.popular ? "text-gray-300" : "text-gray-600"}`}>
                  {tier.description}
                </p>

                <Link
                  to={tier.link}
                  className={`block w-full py-4 rounded-full text-center font-semibold transition-all mb-8 ${
                    tier.popular
                      ? "bg-white text-black hover:bg-gray-100"
                      : "text-white hover:opacity-90"
                  }`}
                  style={!tier.popular ? { backgroundColor: "var(--patreon-coral)" } : undefined}
                >
                  {tier.cta}
                </Link>

                <ul className="space-y-4">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg
                        className="w-6 h-6 flex-shrink-0 mt-0.5"
                        style={{ color: tier.popular ? "var(--patreon-coral)" : "var(--patreon-coral)" }}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className={feature.includes("Everything") ? "font-semibold" : ""}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Frequently asked questions
          </motion.h2>

          <div className="space-y-6">
            {[
              {
                q: "Can I change plans later?",
                a: "Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately."
              },
              {
                q: "What payment methods do you accept?",
                a: "We accept all major credit cards, PayPal, and bank transfers for annual plans."
              },
              {
                q: "Is there a free trial for Pro and Premium?",
                a: "Yes, Pro comes with a 30-day free trial. Premium plans include a personalized demo."
              },
              {
                q: "What are the payment processing fees?",
                a: "Payment processing fees vary by region and payment method, typically 2.9% + $0.30 per transaction."
              },
              {
                q: "Can I cancel anytime?",
                a: "Yes, you can cancel your subscription at any time. No long-term contracts required."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
              >
                <h3 className="text-xl font-semibold mb-3">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4">
        <motion.div
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to start earning?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of creators building sustainable businesses with Patreon
          </p>
          <Link
            to="/register"
            className="inline-block px-8 py-4 rounded-full text-white font-semibold text-lg hover:opacity-90 transition-all"
            style={{ backgroundColor: "var(--patreon-coral)" }}
          >
            Get Started for Free
          </Link>
        </motion.div>
      </section>

      <PatreonFooter />
    </div>
  );
};

export default PricingPage;


